/* eslint-disable @typescript-eslint/no-explicit-any */
import { HeatmapTileLayer as DeckGlHeatmapTileLayer } from '@deck.gl/carto'
import { CHANNEL_SCALES, ALL_FIELD_TYPES } from '@carto/kepler.gl/constants'
import { getDatasetType, getSchemeFromTilesetDataset } from 'features/builder/state/builderSliceUtils'
import { KeplerTable, LayerTypes, TilingScheme } from 'features/builder/types'
import { MapType } from 'features/maps/types'
import { hexToRGB } from 'features/common/utils/colors'
import { getDebounceTime, getTileSize } from 'features/common/utils/cartoDeckGL'
import { cartoSpatialTileLoadOptions } from 'features/common/utils/deckGlWorkers'
import { getSpatialIndexFromGeoColumn } from 'features/builder/state/builderSliceUtils'
import IconHeatmap from 'assets/icons/layers/heatmap.svg?react'
import SpatialIndexLayer, { spatialIndexVisConfigs } from './SpatialIndexLayer'

const heatmapTileVisConfig = {
  ...spatialIndexVisConfigs,
  weightAggregation: 'weightAggregation'
}

export default class HeatmapTileLayer extends SpatialIndexLayer {
  constructor(props = {}) {
    super(props)
    this.registerVisConfig(heatmapTileVisConfig)
  }

  public get type() {
    return LayerTypes.HeatmapTile
  }

  get name() {
    return 'Heatmap'
  }

  get layerIcon() {
    return IconHeatmap
  }

  get visualChannels() {
    return {
      ...super.visualChannels,
      weight: {
        property: 'weight',
        field: 'weightField',
        scale: 'weightScale',
        domain: 'weightDomain',
        key: 'weight',
        defaultMeasure: 'property.density',
        supportedFieldTypes: [ALL_FIELD_TYPES.real, ALL_FIELD_TYPES.integer],
        channelScaleType: CHANNEL_SCALES.identity,
        accessor: 'getWeight',
        defaultValue: () => 1,
        nullValue: 1,
        aggregation: 'weightAggregation'
      }
    }
  }

  validateScale() {
    // Kepler is no ready to validate weight scale so we always return true
    return true
  }

  static findDefaultLayerProps(dataset: KeplerTable, foundLayers) {
    const emptyProps = { props: [] }

    if (foundLayers?.length) {
      return emptyProps
    }
    const scheme = getSchemeFromTilesetDataset(dataset)

    if (getDatasetType(dataset) === MapType.tileset && scheme === TilingScheme.Quadbin) {
      return {
        props: [
          {
            label: dataset.label,
            isVisible: true,
            columns: {}
          }
        ]
      }
    }
    return emptyProps
  }

  renderLayer(opts: any) {
    const { data, gpuFilter } = opts
    if (!data.data) return []

    const { visConfig } = this.config
    const { formatTiles, geoColumn, scheme, sourceType } = this.meta
    const spatialIndex = geoColumn ? getSpatialIndexFromGeoColumn(geoColumn) : null
    const defaultLayerProps = this.getDefaultDeckLayerProps(opts)
    const pickable = this.hasInteractivity
    const updateTriggers = {
      getFilterValue: gpuFilter.filterValueUpdateTriggers
    }

    return [
      new DeckGlHeatmapTileLayer({
        fetch: this.customFetch.bind(this),
        formatTiles,
        binary: true,
        ...defaultLayerProps,
        ...data,
        data: data.data,
        pickable,
        wrapLongitude: true,
        tileSize: getTileSize({
          tileResolution: data.data.tileResolution,
          scheme,
          sourceType,
          spatialIndex
        }),
        debounceTime: getDebounceTime(),
        onViewportLoad: (tiles: any) => this.onViewportLoad(tiles),
        radiusPixels: (visConfig.radius as number) + 20,
        colorRange: visConfig.colorRange?.colors.map(hexToRGB),
        updateTriggers,
        loadOptions: cartoSpatialTileLoadOptions
      })
    ]
  }
}
